// Thin client for the Worker's /api/* endpoints. We deliberately keep this
// dependency-free — no fetch wrappers, no SWR — so the explorer can be
// statically hosted anywhere and only needs VITE_API_BASE.

const BASE =
  (import.meta.env.VITE_API_BASE as string | undefined) ?? "";

export class HttpError extends Error {
  constructor(readonly status: number, readonly path: string) {
    super(({
      400: "Invalid explorer request (HTTP 400). Check the identifier or filters.",
      404: "Not found on this mesh (HTTP 404).",
    } as Record<number, string>)[status] ?? `Explorer request failed (HTTP ${status}). Please try again.`);
    this.name = "HttpError";
  }
}

async function get<T>(path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(`${BASE}${path}`, { signal }).catch((error: unknown) => {
    if (signal?.aborted) throw error;
    throw new Error("Explorer API unreachable. Please try again.");
  });
  // Response bodies/status text are untrusted diagnostics, never member-facing text.
  if (!r.ok) throw new HttpError(r.status, path);
  return (await r.json()) as T;
}

export interface Stats {
  txs_total: number;
  txs_tentative: number;
  txs_final: number;
  txs_invalid: number;
  witnesses_total: number;
  accounts_active_24h: number;
  latest_round: number;
  latest_round_phys_time: number | null;
  indexer_lag_ms: number;
  last_event_at: number | null;
  node_connected: boolean;
  tx_per_sec_1m: number;
  tx_per_sec_5m: number;
}

export interface Tx {
  tx_id: string;
  sender: string | null;
  receiver: string | null;
  kind: number;
  // Server-derived label e.g. "transfer" / "governor-rotation" /
  // "unknown:42" — only present on /api/tx/:id (the recent/round endpoints
  // return the bare row to keep payloads small).
  kind_name?: string;
  amount: string;
  logical_time: string;
  community: string;
  pow: string;
  pubkey: string | null;
  signature: string;
  payload_json: string;
  status: "TENTATIVE" | "FINAL" | "INVALID";
  witness_round: number | null;
  parents: string[];
  received_at: number;
  finalized_at: number | null;
  invalid_reason: number | null;
}

export interface CommunitySummary {
  id: string;
  mesh_node_url: string;
}

export interface CommunityDetail {
  id: string;
  mesh_node_url: string;
  stats: Stats;
}

export interface Witness {
  tx_id: string;
  round: number | null;
  phys_time: number | null;
  witness_pubkey: string | null;
  prev_witness: string | null;
  vrf_output: string | null;
  vrf_proof: string | null;
  tips_json: string | null;
  included_tx_ids_json: string | null;
  received_at: number;
}

export interface AccountResp {
  account: {
    account_id: string;
    pubkey: string | null;
    first_seen_round: number | null;
    credit_limit_micros: string;
    last_logical_time: string;
    tx_count: number;
  } | null;
  balance_micros: string;
  recent_txs: Tx[];
  history: Array<{
    account_id: string;
    round: number;
    balance_micros: string;
    phys_time: number;
  }>;
}

export interface DagSlice {
  nodes: Array<{
    tx_id: string;
    kind: number;
    status: string;
    witness_round: number | null;
    sender: string | null;
    receiver: string | null;
    amount: string;
  }>;
  edges: Array<{ child_tx_id: string; parent_tx_id: string }>;
}

export interface SearchResp {
  txs: Tx[];
  accounts: AccountResp["account"][];
  rounds: Witness[];
}

export interface RecentResp {
  txs: Tx[];
  witnesses: Witness[];
}

export interface CommunityIdentity {
  id: string;
  configured_id: string;
  observed_id: string | null;
  source: "observed" | "configured";
  mismatch: boolean;
}

export interface FleetNode {
  community: string | null;
  membership: "current" | "unknown" | "previous-network" | "retired";
  peer_id: string;
  kind: string;
  location: string | null;
  owner: string | null;
  label: string | null;
  last_health_at: number | null;
  last_event_at: number | null;
  metadata_json: string;
}

export interface FleetResp {
  nodes: FleetNode[];
  history: FleetNode[];
  historical_count: number;
  community: CommunityIdentity;
  now: number;
  online_count: number;
  kind_count: number;
  total: number;
}

export const api = {
  stats: (signal?: AbortSignal) => get<Stats>("/api/stats", signal),
  health: () => get<Stats & { ok: boolean }>("/api/health"),
  tx: (id: string, signal?: AbortSignal) => get<Tx>(`/api/tx/${id}`, signal),
  account: (id: string, signal?: AbortSignal) => get<AccountResp>(`/api/account/${id}`, signal),
  round: (n: number, signal?: AbortSignal) => get<{ witnesses: Witness[]; txs: Tx[] }>(`/api/round/${n}`, signal),
  dag: (since: number, limit: number, signal?: AbortSignal) =>
    get<DagSlice>(`/api/dag?since=${since}&limit=${limit}`, signal),
  search: (q: string, signal?: AbortSignal) =>
    get<SearchResp>(`/api/search?q=${encodeURIComponent(q)}`, signal),
  recent: (limit = 50, signal?: AbortSignal) => get<RecentResp>(`/api/recent?limit=${limit}`, signal),
  witnesses: (sinceRound = 0, limit = 50, signal?: AbortSignal) =>
    get<{ witnesses: Witness[] }>(
      `/api/witnesses?since_round=${sinceRound}&limit=${limit}`, signal,
    ),
  communities: () =>
    get<{ communities: CommunitySummary[] }>("/api/communities"),
  community: (id: string) => get<CommunityDetail>(`/api/communities/${id}`),
  nodes: (signal?: AbortSignal) => get<FleetResp>("/api/nodes", signal),
};

export function microsToCredits(micros: string): string {
  const n = BigInt(micros);
  const sign = n < 0n ? "-" : "";
  const abs = n < 0n ? -n : n;
  const whole = abs / 1_000_000n;
  const frac = abs % 1_000_000n;
  const fracStr = frac.toString().padStart(6, "0").replace(/0+$/, "");
  return fracStr ? `${sign}${whole}.${fracStr}` : `${sign}${whole}`;
}

export function shortHex(s: string | null, head = 6, tail = 4): string {
  if (!s) return "—";
  if (s.length <= head + tail + 1) return s;
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
}

// Canonical TxKind list mirroring `crates/mesh-codec/src/lib.rs`. New kinds
// land here when the codec adds them; unknown kinds fall through with the
// raw integer so the explorer never silently drops events the protocol just
// learned to emit.
const KIND_LABELS: Record<number, string> = {
  0: "transfer",
  1: "witness",
  2: "freeze",
  3: "demurrage",
  4: "snapshot",
  5: "recovery",
  6: "join",
  7: "bond",
  8: "unbond",
  9: "set-credit-limit",
  10: "governor-rotation",
};

export function kindLabel(k: number): string {
  return KIND_LABELS[k] ?? `kind:${k}`;
}

export function isKnownKind(k: number): boolean {
  return Object.prototype.hasOwnProperty.call(KIND_LABELS, k);
}
